import { type AxiosResponse } from 'axios'
import api from '../utils/api'

export interface CompareTarget {
  target_type: 'object' | 'user'
  object_id: string
  /** For "user" targets only — narrows the instructor report to this division/section scope. */
  scope_object_id?: string
}

export interface PostTenantInfoRequest {
  email?: string
}

export interface ResetClassrankedAuthRequest {
  isStudent?: boolean
  isFaculty?: boolean
  delete_for_all?: boolean
}

const getInstructorsBySectionIdApi = async (sectionId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/enrollments/?section_id=${sectionId}`, { params })
}

const getFacultyAndStaffByIdApi = async (userId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/users/${userId}`, { params })
}

const getAllStudentsApi = async (params: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/users/', { params })
}

const getStudentsByTermEnrollmentApi = async (termId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/terms/${termId}/enrollments/`, { params })
}

const postStudentRoleByIdApi = async (userId: string, requestBody: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post(`/api/users/${userId}/role/`, requestBody)
}

const postUserRoleApi = async (requestBody: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post('/api/users/roles/', requestBody)
}

const deleteStudentRoleByIdAndRoleIdApi = async (userId: string, roleId: string, params: Record<string, any>): Promise<AxiosResponse> => {
  return await api.delete(`/api/users/${userId}/role/${roleId}`, { params })
}

const getRoleApi = async (params: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/roles/summary/', params)
}

const getTopBarDivisions = async (): Promise<AxiosResponse> => {
  return await api.get('/api/roles/divisions/')
}

const exportStudentsCSV = async (params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/users/export/?is_student=True', { params })
}

const getUsersSummaryApi = async (params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/users/summary/', { params })
}

export const resetEnrollmentsByTermId = async (termId: string): Promise<AxiosResponse> => {
  return await api.delete(`/api/enrollments/terms/${termId}/reset/`)
}

export const resetInstructorsByTermId = async (termId: string): Promise<AxiosResponse> => {
  return await api.delete(`/api/terms/${termId}/reset/sections/instructors/`)
}

const getUserProfile = async (): Promise<AxiosResponse> => {
  return await api.get('/api/users/profile/')
}

const postTenantInfo = async (
  tenantKey: string,
  requestBody?: PostTenantInfoRequest
): Promise<AxiosResponse> => {
  return await api.post(`/api/tenant_info/${tenantKey}`, requestBody)
}

const resetClassrankedAuth = async (
  params: ResetClassrankedAuthRequest
): Promise<AxiosResponse> => {
  return await api.post('/api/users/reset-cognito/', undefined, { params })
}

export const resetSurveyResponseForUser = async (
  slotId: string,
  sectionId: string,
  userId: string
): Promise<AxiosResponse> => {
  return await api.patch(`/api/slots/${slotId}/sections/${sectionId}/students/${userId}/reset/`)
}

export const getEnrollmentsOfStudent = async (userId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/enrollments/${userId}`, { params })
}

export const getSelfUserReport = async (params: Record<string, any>): Promise<any> => {
  const response = await api.get('/api/users/reports/', { params })
  return response.data
}

export const getUserReport = async (userId: string, params: Record<string, any>): Promise<any> => {
  const response = await api.get(`/api/users/${userId}/reports/`, { params })
  return response.data
}

export const getSelfUserReportCompare = async (data: Record<string, any>, params?: Record<string, any>): Promise<any> => {
  const response = await api.post('/api/users/reports/compare/', data, { params })
  return response.data
}

/** Returns searchable divisions/sections for the current user without requiring a division
 *  scope in the path. Intended for primary instructors who hold a section-level role. */
export const getSelfUserReportSearch = async (params?: Record<string, any>): Promise<any> => {
  const response = await api.get('/api/users/reports/search/', { params })
  return response.data
}

export type ReportAccessLevel = 'full_access' | 'view_results_only'

export interface GenericCompareResult {
  data: any
  reportAccess: ReportAccessLevel | null
}

/**
 * Reads a response header that axios-case-converter may have camelCased, checking both forms —
 * the same reasoning as the content-disposition handling in ReportsPageV2.
 */
const readReportHeader = (
  headers: Record<string, unknown> | undefined,
  camelKey: string,
  wireKey: string
): string | null => {
  const value = headers?.[camelKey] ?? headers?.[wireKey]
  return typeof value === 'string' && value !== '' ? value : null
}

/** Narrows the access header to the two levels the app knows, rather than trusting the string. */
const toReportAccessLevel = (value: string | null): ReportAccessLevel | null =>
  value === 'full_access' || value === 'view_results_only' ? value : null

export const getGenericCompare = async (data: { base_target: CompareTarget, compare_targets: CompareTarget[], slice_ids?: string[], calculations: string[] }, params?: Record<string, any>): Promise<GenericCompareResult> => {
  const response = await api.post('/api/reports/compare/', data, { params })
  const modifiedOn = readReportHeader(response.headers, 'xReportModifiedOn', 'x-report-modified-on')
  return {
    // `modifiedOn` is lifted onto the report payload rather than returned beside it, because that
    // is where `ReportsV2` declares it and where the single consumer already reads it. On this
    // surface the Backend has no body field to put it in: the report endpoints return bare arrays
    // and msgspec calc shapes, so CR-271 Behavior Change #9 sends it as a header — the same reason
    // `X-Report-Access` is one. Absent for multi-compare and Division targets, and for a section
    // with no cached row, all of which resolve to `null`.
    data: modifiedOn != null ? { ...response.data, modifiedOn } : response.data,
    reportAccess: toReportAccessLevel(readReportHeader(response.headers, 'xReportAccess', 'x-report-access'))
  }
}

export const getUserReportHistory = async (userId: string, data: Record<string, any>, params?: Record<string, any>): Promise<any> => {
  const response = await api.post(`/api/users/${userId}/reports/history/`, data, { params })
  return response.data
}

export const getSelfUserReportHistory = async (data: Record<string, any>, params?: Record<string, any>): Promise<any> => {
  const response = await api.post('/api/users/reports/history/', data, { params })
  return response.data
}

export const getSelfUserReportPDFExport = async (params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/users/reports/export/pdf/', { params, responseType: 'blob' })
}

export const getUserReportPDFExport = async (userId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/users/${userId}/reports/export/pdf/`, { params, responseType: 'blob' })
}

export const getSelfUserReportCSVExport = async (params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/users/reports/export/csv/', { params, responseType: 'blob' })
}

export const getUserReportCSVExport = async (userId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/users/${userId}/reports/export/csv/`, { params, responseType: 'blob' })
}

export const getSelfUserReportCommentsExport = async (data: Record<string, any>, params: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post('/api/users/reports/comments/export/csv/', data, { params })
}

export const getUserReportCommentsExport = async (userId: string, data: Record<string, any>, params: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post(`/api/users/${userId}/reports/comments/export/csv/`, data, { params })
}

/**
 * Generic completion-rate endpoint.
 * For instructor targets: target_type='user', object_id=<userId>, scope_object_id=<divisionId>
 * For division targets:   target_type='object', object_id=<divisionId>
 */
export const getGenericCompletionRate = async (params: Record<string, any>): Promise<any> => {
  const response = await api.get('/api/reports/completion_rates/', { params })
  return response.data
}

/**
 * Generic typed completion-rate children endpoint.
 * For instructor targets: target_type='user', object_id=<userId>, scope_object_id=<divisionId>
 * For division targets:   target_type='object', object_id=<divisionId>
 */
export const getGenericCompletionRateChildren = async (categoryName: string, params: Record<string, any>): Promise<any> => {
  return await api.get(`/api/reports/completion_rates/children/${categoryName}/`, {
    headers: { Accept: 'application/cdg' },
    params
  })
}

export default {
  getFacultyAndStaffByIdApi,
  getStudentsByTermEnrollmentApi,
  getInstructorsBySectionIdApi,
  postStudentRoleByIdApi,
  postUserRoleApi,
  deleteStudentRoleByIdAndRoleIdApi,
  getAllStudentsApi,
  getRoleApi,
  exportStudentsCSV,
  getTopBarDivisions,
  getUsersSummaryApi,
  getUserProfile,
  postTenantInfo,
  resetClassrankedAuth,
  getSelfUserReport,
  getUserReport,
  getSelfUserReportCompare,
  getSelfUserReportSearch,
  getGenericCompare,
  getUserReportHistory,
  getSelfUserReportHistory,
  getSelfUserReportPDFExport,
  getUserReportPDFExport,
  getSelfUserReportCSVExport,
  getUserReportCSVExport,
  getSelfUserReportCommentsExport,
  getUserReportCommentsExport,
  getGenericCompletionRate,
  getGenericCompletionRateChildren
}
